/**
 * Landing-flow router for first-time visitors.
 *
 * Decides, once per page load, whether the visitor should get the
 * standard first run (empty Shape view + welcome modal), the
 * collaboration-matched "design together" first run, the feng shui
 * first run that the AI ads promise, or the 3D walk-through first run
 * the 3D ads promise. The decision is deterministic — there is no
 * experiment bucketing — and lives in one pure function
 * ({@link resolveLandingDecision}) so the rule is unit-testable and easy
 * to swap for an A/B assignment later.
 *
 * Rule:
 *
 *   paid-social UTM (or `?flow=collab` / `?flow=fengshui` / `?flow=render3d`)
 *     AND auth resolved as signed-out
 *     AND the canvas is still the empty boot state
 *     AND no `?invite=` / `?shared=` / `?demo=` deep link
 *   → "render_3d" when the ad is a 3D one (`?flow=render3d`, or
 *     `utm_content` like `r3d_*` / `*render3d*` / `*walkthrough*`) and its
 *     kill switch is on; else "feng_shui" when the ad is an AI one
 *     (`?flow=fengshui`, or `utm_content` like `ai_*` / `*feng_shui*` /
 *     `*coffin*`) and its kill switch is on; otherwise "collab" when its
 *     kill switch is on; otherwise "normal". An explicit `?flow=` always
 *     wins over what `utm_content` suggests.
 *
 * Existing signed-in users are always treated normally: the ad may bring
 * them back, but they already know the product and may have a room to
 * resume.
 */

import type { AttributionBundle } from "./attribution";

export type LandingFlow = "normal" | "collab" | "feng_shui" | "render_3d";

/** Why the router picked its flow (reported on `landing_flow_decided`). */
export type LandingReason =
  | "ok"
  | "not_campaign"
  | "deep_link"
  | "flag_off"
  | "auth_pending"
  | "signed_in"
  | "room_started"
  | "seed_failed";

export interface LandingDecision {
  flow: LandingFlow | "pending";
  reason: LandingReason;
}

/** Auth status as exposed by `useAuth()` in `./auth`. */
export type LandingAuthStatus =
  | "loading"
  | "signed-in"
  | "signed-out"
  | "unavailable";

export interface LandingFlowInput {
  /** `window.location.search` (with or without the leading `?`). */
  search: string;
  /** Last-touch (preferred) or first-touch attribution bundle, if any. */
  attribution: AttributionBundle | null;
  authStatus: LandingAuthStatus;
  /** True when the active design already has a room outline. */
  roomStarted: boolean;
  /** Resolved `collab_onboarding` flag. */
  flagEnabled: boolean;
  /** Resolved `feng_shui_onboarding` flag. Omitted = off. */
  fengShuiFlagEnabled?: boolean;
  /** Resolved `render_3d_onboarding` flag. Omitted = off. */
  render3dFlagEnabled?: boolean;
}

/** The `utm_medium` value the campaign playbook mandates for Meta ads. */
export const PAID_SOCIAL_MEDIUM = "paid_social";

/** Explicit override so non-Meta links (and QA) can request a flow. */
export const FLOW_PARAM = "flow";
export const FLOW_COLLAB = "collab";
export const FLOW_FENG_SHUI = "fengshui";
export const FLOW_RENDER_3D = "render3d";
const FLOWS: readonly string[] = [FLOW_COLLAB, FLOW_FENG_SHUI, FLOW_RENDER_3D];

/**
 * `?fs_start=own_room` skips the example and opens the feng shui guide on
 * "draw your room" — the in-app browser handoff lands here.
 */
export const FENG_SHUI_START_PARAM = "fs_start";

/** `utm_content` values that mark an AI (feng shui) ad. */
const FENG_SHUI_CONTENT = /^ai_|feng_?shui|coffin/i;

/** `utm_content` values that mark a 3D walk-through ad. */
const RENDER_3D_CONTENT = /^r3d_|render_?3d|walk_?through/i;

/** Deep-link params that always take precedence over the landing flow. */
const EXCLUDED_PARAMS = ["invite", "shared", "demo"] as const;

function params(search: string): URLSearchParams {
  return new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
}

/**
 * True when this page load is a campaign landing that should be routed
 * into a first run, before considering auth / room state.
 * Note `fbclid` alone does NOT qualify — organic Facebook/Instagram link
 * clicks carry it too, and those visitors did not see the ad.
 */
export function isCampaignLanding(
  search: string,
  attribution: AttributionBundle | null
): boolean {
  const sp = params(search);
  const flow = sp.get(FLOW_PARAM);
  if (flow && FLOWS.includes(flow)) return true;
  const medium = sp.get("utm_medium") ?? attribution?.utm_medium ?? null;
  return medium?.toLowerCase() === PAID_SOCIAL_MEDIUM;
}

/** True when the landing belongs to an AI (feng shui) ad. */
export function isFengShuiLanding(
  search: string,
  attribution: AttributionBundle | null
): boolean {
  const flow = params(search).get(FLOW_PARAM);
  if (flow === FLOW_FENG_SHUI) return true;
  if (flow && FLOWS.includes(flow)) return false;
  return FENG_SHUI_CONTENT.test(landingUtmContent(search, attribution) ?? "");
}

/** True when the landing belongs to a 3D walk-through ad. */
export function isRender3dLanding(
  search: string,
  attribution: AttributionBundle | null
): boolean {
  const flow = params(search).get(FLOW_PARAM);
  if (flow === FLOW_RENDER_3D) return true;
  if (flow && FLOWS.includes(flow)) return false;
  return RENDER_3D_CONTENT.test(landingUtmContent(search, attribution) ?? "");
}

/** True when a deep-link param owns this page load. */
export function hasExcludedDeepLink(search: string): boolean {
  const sp = params(search);
  return EXCLUDED_PARAMS.some((k) => sp.has(k));
}

/**
 * Pure routing decision with the reason it was made. `flow` is
 * `"pending"` while auth is still restoring so the caller can keep
 * showing a splash; the caller is responsible for capping that wait
 * (see `CampaignLandingGate`).
 */
export function resolveLandingDecision(input: LandingFlowInput): LandingDecision {
  if (hasExcludedDeepLink(input.search)) return { flow: "normal", reason: "deep_link" };
  if (!isCampaignLanding(input.search, input.attribution)) {
    return { flow: "normal", reason: "not_campaign" };
  }
  const render3d = input.render3dFlagEnabled === true && isRender3dLanding(input.search, input.attribution);
  const fengShui =
    !render3d && input.fengShuiFlagEnabled === true && isFengShuiLanding(input.search, input.attribution);
  const candidate: LandingFlow | null = render3d
    ? "render_3d"
    : fengShui
      ? "feng_shui"
      : input.flagEnabled
        ? "collab"
        : null;
  if (!candidate) return { flow: "normal", reason: "flag_off" };
  if (input.authStatus === "loading") return { flow: "pending", reason: "auth_pending" };
  if (input.authStatus === "signed-in") return { flow: "normal", reason: "signed_in" };
  if (input.roomStarted) return { flow: "normal", reason: "room_started" };
  return { flow: candidate, reason: "ok" };
}

/** {@link resolveLandingDecision} without the reason. */
export function resolveLandingFlow(input: LandingFlowInput): LandingFlow | "pending" {
  return resolveLandingDecision(input).flow;
}

/** Where the feng shui first run starts: the example, or the visitor's own room. */
export function fengShuiStartPhase(search: string): "example" | "own_room" {
  return params(search).get(FENG_SHUI_START_PARAM) === "own_room" ? "own_room" : "example";
}

/** Read `utm_content` from the URL first, then the attribution bundle. */
export function landingUtmContent(
  search: string,
  attribution: AttributionBundle | null
): string | null {
  const fromUrl = params(search).get("utm_content");
  if (fromUrl && fromUrl.length > 0) return fromUrl.slice(0, 200);
  return attribution?.utm_content ?? null;
}
